import type { Ref } from "vue";
import { ref } from "vue";
import { DataLoadingState } from "@/model/DataLoadingState";
import { CardType } from "./apiDashboardWrappers";
import { useAppConfigStore } from "@/stores/appConfigStore";
import router from "@/router";

const userIconColors = {
    default: '#57afd6',
    a: '#66c562',
    b: '#e7bd36',
    c: '#e18928',
    d: '#db3e2e',
    e: '#db354a',
    f: '#4749be',
    g: '#337af5',
    h: '#67b6e5',
    i: '#af52de',
    j: '#009688',
    k: '#8bc34a',
    l: '#cddc39',
    m: '#ff5722',
    n: '#9c27b0',
    o: '#673ab7',
    p: '#607d8b',
    q: '#8e8e93',
    r: '#a5915f',
    s: '#00bcd4',
    t: '#66c562',
    u: '#e7bd36',
    v: '#e18928',
    w: '#db3e2e',
    x: '#db354a',
    y: '#4749be',
    z: '#337af5'
}
type UserIconColorKey = keyof typeof userIconColors;

const cardImagesMap = {
    [CardType.Visa]: "card-visa.svg",
    [CardType.MasterCard]: "card-mastercard.svg",
    [CardType.Discover]: "card-discover.svg",
    [CardType.AmericanExpress]: "card-amex.svg",
    [CardType.Jcb]: "card-jcb.svg",
    [CardType.DinersClub]: undefined,
    [CardType.Bancontact]: undefined,
};
type CardImageKey = keyof typeof cardImagesMap;

export function getUserIconColor(userName: string) {
    const iconKey = userName.length > 0 ? userName[0].normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase() : "";
    const iconColor = iconKey in userIconColors ? userIconColors[iconKey as UserIconColorKey] : userIconColors.default;
    return iconColor;
}

export function getCreditCardImageName(cardType: CardType) {
    const cardImageName = cardType in cardImagesMap ? cardImagesMap[cardType as CardImageKey] : undefined;
    return cardImageName ? getPublicImageUrl(`/images/cards/${cardImageName}`) : undefined;
}

export function getPublicImageUrl(path?: string) {
    if (path == null) {
        return undefined;
    }
    const baseUrl = import.meta.env.BASE_URL;
    const pathNormalized = path.replace(/^([/]?images[/])?/, "");
    const imageUrl = `${baseUrl}images/${pathNormalized}`;
    return imageUrl;
}

export function generateUniqueId(prefix: string = "") {
    const randomId = Math.random().toString(36).substring(2, 12);
    return `${prefix}_${randomId}`;
}

export function getInvoiceStatusClass(status?: string) {
    const statusText = status?.toLocaleLowerCase();
    switch (statusText) {
        case 'paid in full':
            return 'tag _style-a';
        case 'open':
            return 'tag _style-b';
        case 'paid':
            return 'tag _style-c';
        default:
            return 'tag';
    }
}

export async function resolveApiPromiseDeprecated<T>(apiPromise: Promise<T>, targetRef: Ref<T>, loadingStateRef: Ref<DataLoadingState>, defaultValue: T) {
    targetRef.value = defaultValue;
    loadingStateRef.value = DataLoadingState.inProgress;

    try {
        targetRef.value = await apiPromise;
        loadingStateRef.value = DataLoadingState.success;
    }
    catch (exception) {
        loadingStateRef.value = DataLoadingState.error;
        throw exception;
    }
}

export function sortString(a?: string, b?: string, caseIgnore: boolean = false) {
    if (a == null)
        return -1;
    if (b == null)
        return 1;
    const a2 = caseIgnore ? a.toUpperCase() : a;
    const b2 = caseIgnore ? b.toUpperCase() : b;
    if (a2 === b2)
        return 0;
    return a2 < b2 ? -1 : 1;
}

export function getExtLinks() {
    const appConfigStore = useAppConfigStore();
    const extLinksObject = appConfigStore.appConfig.extLinks;

    const storeFlowStart = `${extLinksObject.store}/flowmanager/flow/start`;

    return {
        store: extLinksObject.store,
        activateWorkspace: extLinksObject.activateWorkspace,
        accountBaseUrl: extLinksObject.accountBaseUrl,
        oldDashboardBaseUrl: extLinksObject.oldDashboardBaseUrl,
        userProfileBaseUrl: extLinksObject.userProfileBaseUrl,
        supportCenterUrl: extLinksObject.supportCenterUrl,
        forumUrl: extLinksObject.forumUrl,
        damsUrl: extLinksObject.damsUrl,

        getAddLicenseSeatUrl: (licenseId: string, count: number) => enrichWithTrackingId(`${storeFlowStart}?Action=AddLicenseSeat&Licenses=${licenseId}&Quantity=${count}`),
        getUpgradeSubscrLevelUrl: (level: number) => enrichWithTrackingId(`${storeFlowStart}?action=UpgradeSubscriptionLevel&ToSubscriptionLevelId=${level}`),
        getUpgradeSubscrLevelQuoteUrl: (level: number) => enrichWithTrackingId(`${storeFlowStart}?action=UpgradeSubscriptionLevelQuote&ToSubscriptionLevelId=${level}`),
        tradeUpUrl: (licenseId: string) => enrichWithTrackingId(`${storeFlowStart}?ProductId=ADTBTRADEIN&DeactivateLicenses=${licenseId}`),
        upgradeGeoScopeUrl: (licenseId: string, targetGeoScopeId: string) => enrichWithTrackingId(`${storeFlowStart}?Action=UpgradeGeoScope&Licenses=${licenseId}:${targetGeoScopeId}`),
    }
}

export function enrichWithTrackingId(url?: string) {
    if (url == null)
        return url;

    const trackingIdParamName = "TrackingId";
    const trackingIdParamValue = "ALDASHB";
    const regex = new RegExp(`[?&]${trackingIdParamName}=`);
    if (regex.test(url))
        return url;

    const separator = url.indexOf("?") === -1 ? "?" : "&";
    return `${url}${separator}${trackingIdParamName}=${trackingIdParamValue}`;
}

export const licenseRequestNotifications = ref({
    addSeat: 'User(s) requesting a seat',
    renew: 'User(s) requesting a renewal',
    tradeUp: 'User(s) requesting an upgrade',
    access: 'Non-group user(s) requesting access to a license'
});

export function redirectToAuthentication(redirectUrl?: string) {
    const appConfigStore = useAppConfigStore();
    const currentUrl = router.resolve(router.currentRoute.value);
    const absoluteURL = new URL(redirectUrl ?? currentUrl.href, window.location.origin).href;

    const authUrl: string = appConfigStore.appConfig.authUrl;
    window.location.href = authUrl.replace("{0}", absoluteURL);
}

export function areEqualArrays<T>(array1: T[], array2: T[], comparator?: (a: T, b: T) => number) {
    if (array1.length !== array2.length)
        return false;

    const arr1 = array1.concat().sort(comparator);
    const arr2 = array2.concat().sort(comparator);

    for (let i = 0; i < arr1.length; i++) {
        if (arr1[i] !== arr2[i]) {
            return false;
        }
    }

    return true;
}

export async function triggerDownloadContent(blob: Blob, fileName: string) {
    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = fileName;
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    link.remove();
}

export function isHeadlessBrowser(): boolean | undefined {
    // Headless browser has the flag 'webdriver'
    if (navigator.webdriver) {
        return true;
    }
    if (/headless/i.test(navigator.userAgent)) {
        return true;
    }
    if (/headless/i.test(navigator.appVersion)) {
        return true;
    }
    return undefined;
}
